/**
 * Module-scoped pub/sub for "invoices were just sent" (KAN-746), same shape and
 * rationale as creditsMutationSignal: the bulk-send run lives in an app-level
 * provider that does not own the dashboard's `invoices` state, and billing has
 * no realtime. The mounted dashboard (admin OR billing — never both) subscribes
 * via useInvoiceSentSubscription and applies the patch / scoped reconcile to its
 * own array. Payload is a HINT of what the WRITE already proved, never fetched
 * data to trust.
 *
 * kinds:
 *   { kind: 'patch', ids, sentTo? }  — optimistic status flip, fired per success
 *   { kind: 'reconcile', ids }       — ONE quiet scoped re-read at the end of a run
 */
const listeners = new Set();

export function subscribeInvoiceSent(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function emitInvoiceSent(detail) {
  [...listeners].forEach((fn) => {
    try { fn(detail); } catch (err) { console.warn('[invoiceSentSignal] listener threw', err); }
  });
}

// Recently-sent registry (KAN-746 review): a reconcile that READ a draft just
// before its send committed must never downgrade the optimistic 'sent' row.
// id -> epoch ms. Cleared at sign-out (clearSignedOutMemoryState).
export const RECENT_SENT_WINDOW_MS = 30000;
const recentlySent = new Map();

export function markInvoicesSent(ids, now = Date.now()) {
  (ids || []).forEach((id) => { if (id != null) recentlySent.set(String(id), now); });
}

export function wasRecentlySent(id, now = Date.now()) {
  const at = recentlySent.get(String(id));
  return at != null && now - at < RECENT_SENT_WINDOW_MS;
}

export function clearRecentlySent() { recentlySent.clear(); }
